import { useAtomValue, useSetAtom } from 'jotai';
import { useRef } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { Button, SkeletonRect,Stack, Text, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { formatTimestampToDate } from '@common/lib/time';

import { useCompleteMSO } from '../../../api/progress/useCompleteMSO';
import { useGetCompleteMSO } from '../../../api/progress/useGetCompleteMSO';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

import { fileToBase64 } from './utils/fileToBase64';

interface CompleteMSODetailProps {
	actionItem: ActionItem;
}

interface FormData {
	msoCompleteScan: File | null;
}

/**
 * CompleteMSODetail component for completing MSO.
 * Displays a file upload input for MSO complete scan and a submit button.
 */
export const CompleteMSODetail = ({ actionItem }: CompleteMSODetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const fileInputRef = useRef<HTMLInputElement>(null);

	const completeMSOMutation = useCompleteMSO(orderId);
	const { data: existingData, isLoading: isLoadingData } = useGetCompleteMSO(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
		watch,
	} = useForm<FormData>({
		defaultValues: {
			msoCompleteScan: null,
		},
	});

	const selectedFile = watch('msoCompleteScan');

	const submitHandler = handleSubmit(async (data: FormData) => {
		if (!data.msoCompleteScan) {
			return;
		}

		try {
			const base64Data = await fileToBase64(data.msoCompleteScan);

			await completeMSOMutation.mutateAsync({
				msoCompleteScan: {
					base64Data,
					type: data.msoCompleteScan.type,
					name: data.msoCompleteScan.name,
				},
			});

			toast({
				message: 'MSO completed successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form
			reset();
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to complete MSO';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Upload a scanned copy of the completed MSO. Use details below to complete the MSO transfer to the buyer and upload a scan of the completed document.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{/* Show completion status if already complete */}
				{isLoadingData ? (
					<SkeletonRect width='100%' height={48} />
				) : existingData?.msoComplete ? (
					<Stack itemsSpacing='4'>
						<Text size='body' color='success'>
							✓ MSO completed
						</Text>
						{existingData.msoCompleteDate && (
							<Text size='bodySmall' color='secondary'>
								Completed on{' '}
								{formatTimestampToDate(new Date(existingData.msoCompleteDate).getTime()) ?? '—'}
							</Text>
						)}
					</Stack>
				) : null}

				{/* Show file input (allow re-upload even if complete) */}
				<Controller
					name='msoCompleteScan'
					control={control}
					rules={{
						required: 'Please select an MSO complete scan file',
					}}
					render={({ field: { onChange } }) => (
						<Stack itemsSpacing='4'>
							<Text size='footnote'>MSO Complete Scan</Text>
							<input
								ref={fileInputRef}
								type='file'
								accept='.pdf,.png,.jpg,.jpeg'
								onChange={(event) => {
									const file = event.target.files?.[0];
									if (file) {
										onChange(file);
									}
								}}
								disabled={isSubmitting || completeMSOMutation.isPending}
								style={{
									padding: '8px',
									border: errors.msoCompleteScan
										? '1px solid red'
										: '1px solid #ccc',
									borderRadius: '4px',
									width: '100%',
								}}
							/>
							{errors.msoCompleteScan && (
								<Text size='footnote' color='error'>
									{errors.msoCompleteScan.message}
								</Text>
							)}
							{selectedFile && (
								<Text size='footnote' color='secondary'>
									Selected: {selectedFile.name}
								</Text>
							)}
						</Stack>
					)}
				/>

				<Button
					size='large'
					isFullWidth
					onPress={() => {
						submitHandler();
					}}
					isLoading={isSubmitting || completeMSOMutation.isPending}
					isDisabled={!selectedFile || isLoadingData}>
					{actionItem.config.buttonTitle || 'Upload completed MSO'}
				</Button>
			</Stack>
		</Stack>
	);
};
